import jsPDF from "jspdf"
import autoTable, { type CellHookData } from "jspdf-autotable"
import type { TFunction } from "i18next"

import type {
  CouplingLoadView,
  UsinageDetail,
} from "@/features/milling/api/types"

import {
  type CouplageReportData,
  buildCouplageReport,
  reportSourceKey,
} from "./couplage-report"
import {
  PDF_SECTION_BAND,
  PDF_TEXT_MUTED,
  PDF_TEXT_PRIMARY,
  PDF_TEXT_SECONDARY,
  PDF_TOTAL_BAND,
  PDF_TYPE,
  formatPdfNumber,
  pdfHeadStyles,
  pdfTableStyles,
} from "./pdf-shared"

// downloadCouplageRapportPdf renders the chargement × planned-lot
// matrix on landscape A4. Two-tier header band, body rows per
// chargement, TOTAL SACS footer row. Planning artifact only — does
// not include fabricated weights or swapped-in lot numbers.
export function downloadCouplageRapportPdf(
  usinage: UsinageDetail,
  loads: CouplingLoadView[],
  t: TFunction<"milling">,
) {
  const couplage = buildCouplageReport(usinage, loads)
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" })
  drawCouplagePage(doc, couplage, t)
  drawFooter(doc, t)
  doc.save(`${couplage.usinageNumber}-couplage.pdf`)
}

function drawCouplagePage(
  doc: jsPDF,
  couplage: CouplageReportData,
  t: TFunction<"milling">,
) {
  const margin = 28
  const pageWidth = doc.internal.pageSize.getWidth()

  // Title — short top line + smaller centred subtitle below so long
  // usinage numbers never overflow the right margin.
  doc.setFont("helvetica", PDF_TYPE.title.weight)
  doc.setFontSize(PDF_TYPE.title.size)
  doc.setTextColor(PDF_TEXT_PRIMARY)
  doc.text(t("couplageReport.title"), pageWidth / 2, margin + 14, {
    align: "center",
  })

  doc.setFont("helvetica", PDF_TYPE.meta.weight)
  doc.setFontSize(PDF_TYPE.meta.size)
  doc.setTextColor(PDF_TEXT_SECONDARY)
  const subtitle = t("couplageReport.headerSubtitle", {
    usinageNumber: couplage.usinageNumber,
    from: couplage.dateRange.from,
    to: couplage.dateRange.to,
  })
  doc.text(subtitle, pageWidth / 2, margin + 30, { align: "center" })
  doc.setTextColor(PDF_TEXT_PRIMARY)

  drawCouplageMatrix(doc, couplage, t, margin, margin + 46)
}

function drawCouplageMatrix(
  doc: jsPDF,
  couplage: CouplageReportData,
  t: TFunction<"milling">,
  margin: number,
  y: number,
) {
  const leftColCount = 5

  const headTop = [
    {
      content: t("couplageReport.leftHeader"),
      colSpan: leftColCount,
      styles: {
        halign: "center" as const,
        fillColor: PDF_SECTION_BAND,
        fontStyle: "bold" as const,
      },
    },
    {
      content: t("couplageReport.lotsHeader"),
      colSpan: couplage.lots.length + 1,
      styles: {
        halign: "center" as const,
        fillColor: PDF_SECTION_BAND,
        fontStyle: "bold" as const,
      },
    },
  ]

  const headBottom = [
    t("rapport.cols.fournisseur"),
    t("rapport.cols.cccNumber"),
    t("couplageReport.cols.nbreSacs"),
    t("couplageReport.cols.poidsNet"),
    t("couplageReport.cols.poidsMoyenne"),
    ...couplage.lots.map((l) => l.label),
    t("couplageReport.cols.reste"),
  ]

  const body = couplage.sources.map((s) => {
    const key = reportSourceKey(s.kind, s.id)
    const left: (string | number)[] = [
      s.fournisseur,
      s.cccNumber,
      formatPdfNumber(s.nbreSacs),
      formatPdfNumber(s.poidsNetKg, 2),
      formatPdfNumber(s.poidsMoyenneKg, 2),
    ]
    const matrix = couplage.lots.map((l) => {
      const bags = l.bagsBySource[key] ?? 0
      return bags > 0 ? formatPdfNumber(bags) : ""
    })
    const reste = couplage.resteBySource[key] ?? 0
    return [...left, ...matrix, reste > 0 ? formatPdfNumber(reste) : ""]
  })

  // TOTAL SACS row.
  const totalRow = [
    {
      content: t("couplageReport.totalSacs"),
      colSpan: 2,
      styles: {
        halign: "left" as const,
        fillColor: PDF_TOTAL_BAND,
        fontStyle: "bold" as const,
      },
    },
    // Bag totals are integers; weight totals get 2 decimals to match
    // the per-row precision.
    {
      content: formatPdfNumber(couplage.totalNbreSacs),
      styles: {
        halign: "right" as const,
        fillColor: PDF_TOTAL_BAND,
        fontStyle: "bold" as const,
      },
    },
    {
      content: formatPdfNumber(couplage.totalPoidsNetKg, 2),
      styles: {
        halign: "right" as const,
        fillColor: PDF_TOTAL_BAND,
        fontStyle: "bold" as const,
      },
    },
    {
      content: formatPdfNumber(couplage.totalPoidsMoyenneKg, 2),
      styles: {
        halign: "right" as const,
        fillColor: PDF_TOTAL_BAND,
        fontStyle: "bold" as const,
      },
    },
    ...couplage.lots.map((l) => ({
      content: formatPdfNumber(couplage.totalBagsByLot[l.id] ?? 0),
      styles: {
        halign: "right" as const,
        fillColor: PDF_TOTAL_BAND,
        fontStyle: "bold" as const,
      },
    })),
    {
      content: formatPdfNumber(couplage.totalResteSacs),
      styles: {
        halign: "right" as const,
        fillColor: PDF_TOTAL_BAND,
        fontStyle: "bold" as const,
      },
    },
  ]

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [headTop, headBottom],
    body,
    foot: [totalRow],
    styles: {
      ...pdfTableStyles(),
      fontSize: 8,
      cellPadding: 4,
    },
    headStyles: pdfHeadStyles(),
    columnStyles: {
      2: { halign: "right" as const },
      3: { halign: "right" as const },
      4: { halign: "right" as const },
      ...Object.fromEntries(
        couplage.lots.map((_, i) => [
          5 + i,
          { halign: "right" as const },
        ]),
      ),
      [5 + couplage.lots.length]: { halign: "right" as const },
    },
    didParseCell: (cell: CellHookData) => {
      if (
        cell.section === "body" &&
        (cell.column.index === 0 || cell.column.index === 1)
      ) {
        cell.cell.styles.halign = "left"
      }
    },
  })
}

function drawFooter(doc: jsPDF, t: TFunction<"milling">) {
  const pageHeight = doc.internal.pageSize.getHeight()
  const pageWidth = doc.internal.pageSize.getWidth()
  doc.setFont("helvetica", PDF_TYPE.footer.weight)
  doc.setFontSize(PDF_TYPE.footer.size)
  doc.setTextColor(PDF_TEXT_MUTED)
  doc.text(
    t("rapport.pageFooter", { page: 1, total: 1 }),
    pageWidth / 2,
    pageHeight - 18,
    { align: "center" },
  )
  doc.setTextColor(PDF_TEXT_PRIMARY)
}
